import { FC } from 'react';
import { toast } from 'sonner';
import { Button } from '@repo/ui/components/ui/button';
import {
  useCandidate,
  useCandidates,
  useCandidateSelection,
  useStages,
  useUser,
} from '@/state';
import { permissions } from '@repo/api/permify/constants';
import { ConfirmationDialog } from '@repo/ui/components/ui/confirmation-dialog';
import { Trash2 } from 'lucide-react';
import { showErrorToast } from '../../../ui/error-toast';

type DismissCandidateProps = {
  className?: string;
  candidateIds: Array<string>;
  showIcon?: boolean;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
};

export const DismissCandidate: FC<DismissCandidateProps> = ({
  className,
  candidateIds,
  variant = 'outline',
  showIcon,
}) => {
  const { stages, changeCandidateCount } = useStages();
  const { deselectAll } = useCandidateSelection();
  const candidate = useCandidate(candidateIds[0] as string);
  const {
    params: {
      filters: { stageId, openCallId },
    },
    moveToStage,
  } = useCandidates();

  const session = useUser();
  const targetStageId = '0';
  const findStage = (id: string) => stages.find((stage) => stage.id === id);
  const moveCandidates = async () => {
    try {
      const count = await moveToStage(
        candidateIds,
        targetStageId,
        session?.user?.sub || '',
      );
      changeCandidateCount({
        [stageId]: findStage(stageId)!.count - count,
        [targetStageId]: findStage(targetStageId)!.count + count,
      });
      deselectAll();
      const targetStage = findStage(targetStageId);
      const toastMessage = (
        <span className="text-sm font-normal text-cn-foreground">
          {candidateIds.length === 1
            ? candidate?.profile.firstName
            : candidateIds.length + ' candidates'}{' '}
          <a
            className="underline"
            href={`/open-calls/${openCallId}/stage/${targetStageId}`}
          >
            {targetStage?.name}.
          </a>
        </span>
      );
      toast(
        <>
          {toastMessage}
          {/* TODO: Enable `Undo` button after MVP */}
          <Button variant="outline" className="hidden ml-4">
            Undo
          </Button>
        </>,
        {
          className: 'pl-6 pr-8 py-6',
        },
      );
    } catch (error) {
      showErrorToast({ title: 'Unable to dismiss candidate' });
      console.error('Failed to dismiss:', error);
    }
  };
  const permission = permissions.openCalls.removeCandidates;

  return (
    <ConfirmationDialog
      title="Dismiss candidates"
      description={`${
        candidateIds.length === 1
          ? candidate?.profile.firstName
          : candidateIds.length + ' candidates'
      } will be dismissed.`}
      actionName="Dismiss"
      action={moveCandidates}
      permission={permission}
    >
      <Button
        data-testid="dismiss-button"
        variant={variant}
        data-permission={permission}
        className={className}
      >
        Dismiss {showIcon && <Trash2 role="trash-icon" />}
      </Button>
    </ConfirmationDialog>
  );
};
